import { useState, useEffect, useContext } from "react";
import Swal from "sweetalert2";
import AppointmentsApi from "../../../../Classes/API/Appointments/AppointmentsApi";
import SearchStringOrIntReq from "../../../../Classes/Models/Requests/SearchStringOrIntReq";
import RemoveTherapistFromDayReq from "../../../../Classes/Models/Requests/Appointments/RemoveTherapistFromDayReq";
import LinkAccSessToAppHourReq from "../../../../Classes/Models/Requests/Appointments/LinkAccSessToAppHourReq";
import { SYSTEM_COLORS } from "../../../../utils/themeConstants";
import { UserContext } from "App";

export const useAppDayLogic = (props) => {
  const {
    AppDay,
    setAppDay,
    setIsLoaded,
    GetAppDayById,
    IsLoaded,
    DayPartitionNumber,
    setDayPartitionNumber,
    id,
    search,
    navigate,
    state,
    translations,
  } = props;

  const CurrentUser = useContext(UserContext);
  // --- States ---
  const [ShowSideBar, setShowSideBar] = useState(false);
  const [ShowCreateAppointment, setShowCreateAppointment] = useState(false);
  const [SelectedAppHour, setSelectedAppHour] = useState({});
  const [FacilityAccSession, setFacilityAccSession] = useState({});
  const [ShowFacilityModal, setShowFacilityModal] = useState(false);
  const [MovingSession, _setMovingSession] = useState({});
  const [Reminders, setReminders] = useState([]);
  const [isInnerLoaded, setIsInnerLoaded] = useState(true);
  const [SearchString, setSearchString] = useState("");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [removingTherapist, setRemovingTherapist] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showEvalModal, setShowEvalModal] = useState(false);
  const [showDummyModal, setShowDummyModal] = useState(false);
  const [dummySessionsList, setDummySessionsList] = useState([]);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isOperationsEnabled, setIsOperationsEnabled] = useState(() => {
    const autoOpenRoles = ["Admin", "Owner", "HeadAccounting", "Therapist"];
    return autoOpenRoles.includes(CurrentUser?.userType);
  });

  const [zoomLevel, setZoomLevel] = useState(() => {
    const width = window.innerWidth;
    if (width < 600) return 0.4;
    if (width < 1200) return 0.65;
    return 0.8;
  });

  // --- Side Effects ---
  useEffect(() => {
    const sideNav = document.getElementById("TheSidenav");
    if (sideNav) sideNav.style.display = ShowSideBar ? "block" : "none";
    return () => {
      if (sideNav) sideNav.style.display = "block";
    };
  }, [ShowSideBar]);

  useEffect(() => {
    const handleScroll = () => setIsCollapsed(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (AppDay?.id) GetDayReminders();
  }, [AppDay?.id]);

  useEffect(() => {
    if (DayPartitionNumber && AppDay?.id) {
      GetAppDayById(AppDay.id, DayPartitionNumber);
      setIsInnerLoaded(false);
    }
  }, [DayPartitionNumber]);

  useEffect(() => {
    const allDummy = [];
    AppDay?.getTherapists?.forEach((t) => {
      t.getAppointmentHours?.forEach((h) => {
        h.getListedAccountingSessionsDummy?.forEach((s) => {
          allDummy.push({ ...s, therapistName: t.therapistName, time: h.hour });
        });
      });
    });
    setDummySessionsList(allDummy);
    if (AppDay != null) setIsInnerLoaded(true);
  }, [AppDay]);

  // --- Logic Functions ---
  const GetDayReminders = async () => {
    if (!AppDay?.id) return;
    const req = new SearchStringOrIntReq();
    req.SearchInt = AppDay.id;
    const res = await AppointmentsApi.GetDayReminders(req);
    if (res.status === "Success") setReminders(res.data);
  };

  const onSearch = async () => {
    Swal.fire({ title: "Please wait", didOpen: () => Swal.showLoading() });
    const searchString = SearchString.trim().toLowerCase();
    if (searchString === "") {
      await GetAppDayById(id, DayPartitionNumber);
      Swal.close();
      return;
    }
    const filteredTherapists = AppDay.getTherapists
      .map((therapist) => {
        const filteredHours = therapist.getAppointmentHours
          .map((hour) => {
            const filteredSessions = hour.getListedAccountingSessions.filter(
              (session) =>
                session.therapistName.toLowerCase().includes(searchString) ||
                session.patientName.toLowerCase().includes(searchString),
            );
            return { ...hour, getListedAccountingSessions: filteredSessions };
          })
          .filter((hour) => hour.getListedAccountingSessions.length > 0);
        return { ...therapist, getAppointmentHours: filteredHours };
      })
      .filter((therapist) => therapist.getAppointmentHours.length > 0);

    setAppDay({ ...AppDay, getTherapists: filteredTherapists });
    Swal.close();
  };

  const removeTherapistFromDay = async (therapistDay) => {
    if (removingTherapist) return;

    const hasAppointments = therapistDay.getAppointmentHours?.some(
      (ah) => ah.getAppointments && ah.getAppointments.length > 0,
    );

    if (hasAppointments) {
      await Swal.fire({
        title:
          translations.appointmentTableCannotRemoveTherapist || "Cannot Remove",
        text:
          translations.appointmentTableTherapistHasAppointments ||
          "This therapist has scheduled appointments and cannot be removed.",
        icon: "error",
        confirmButtonColor: SYSTEM_COLORS.info,
      });
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to remove ${therapistDay.therapistName} from this day.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: SYSTEM_COLORS.danger,
      cancelButtonColor: SYSTEM_COLORS.secondary,
      confirmButtonText: "Yes, remove",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setRemovingTherapist(therapistDay.therapistId);
    try {
      const req = new RemoveTherapistFromDayReq();
      req.dayId = AppDay.id;
      req.therapistId = therapistDay.therapistId;

      const res = await AppointmentsApi.RemoveTherapistFromAppointmentDay(req);

      if (res.status === "Success") {
        await Swal.fire({
          title: "Removed!",
          text: "Therapist has been removed successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });

        setIsLoaded(false);
        await GetAppDayById(AppDay.id);
      } else {
        throw new Error(res.message || "Failed to remove therapist");
      }
    } catch (error) {
      console.error("Remove Therapist Error:", error);

      await Swal.fire({
        title: "Error",
        text:
          error.message ||
          "An unexpected error occurred while removing the therapist.",
        icon: "error",
        confirmButtonColor: SYSTEM_COLORS.primary,
      });
    } finally {
      setRemovingTherapist(null);
    }
  };

  const PasteSession = async (appHour) => {
    if (!MovingSession.id) return;
    const req = new LinkAccSessToAppHourReq(
      MovingSession.id,
      appHour.id,
      false,
      false,
    );
    const res =
      await AppointmentsApi.LinkAccountingSessionToAppointmentHour(req);
    if (res.status === "Success") {
      _setMovingSession({});
      GetAppDayById(AppDay.id, DayPartitionNumber);
    }
  };

  const ClearAppointmentDay = async () => {
    const confirmation = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, clear it!",
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    Swal.fire({
      icon: "info",
      title: "Clearing Appointments...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    const req = new SearchStringOrIntReq();
    req.SearchInt = AppDay.id;

    const res = await AppointmentsApi.ClearAppointmentDay(req);

    if (res.status === "Success") {
      Swal.fire("Success", res.message, "success");
      setIsLoaded(false);
      GetAppDayById(AppDay.id);
    } else {
      Swal.fire("Failed", res.message, "error");
    }
  };
  const handlePartitionChange = (newSlot) => {
    setDayPartitionNumber(newSlot);
    const queryParams = new URLSearchParams(search);
    queryParams.set("slot", newSlot);
    navigate(`?${queryParams.toString()}`, { replace: true, state: state });
  };
  const AddSessionToAppointmentHour = (session, appHour) => {
    setAppDay((prevAppDay) => {
      return {
        ...prevAppDay,
        getTherapists: prevAppDay.getTherapists.map((th) => {
          return th.id === appHour.appointmentDayTherapistId
            ? {
                ...th,
                getAppointmentHours: th.getAppointmentHours.map((aH) =>
                  aH.id === appHour.id
                    ? {
                        ...aH,
                        getListedAccountingSessions: [
                          ...(aH.getListedAccountingSessions ?? []),
                          session,
                        ],
                      }
                    : aH,
                ),
              }
            : th;
        }),
      };
    });
  };

  return {
    ShowSideBar,
    setShowSideBar,
    ShowCreateAppointment,
    setShowCreateAppointment,
    SelectedAppHour,
    setSelectedAppHour,
    FacilityAccSession,
    setFacilityAccSession,
    ShowFacilityModal,
    setShowFacilityModal,
    MovingSession,
    _setMovingSession,
    Reminders,
    isInnerLoaded,
    SearchString,
    setSearchString,
    zoomLevel,
    setZoomLevel,
    isCollapsed,
    setIsCollapsed,
    removingTherapist,
    showTransferModal,
    setShowTransferModal,
    showEvalModal,
    setShowEvalModal,
    showDummyModal,
    setShowDummyModal,
    isOrderModalOpen,
    setIsOrderModalOpen,
    isOperationsEnabled,
    setIsOperationsEnabled,
    dummySessionsList,
    onSearch,
    removeTherapistFromDay,
    PasteSession,
    ClearAppointmentDay,
    handlePartitionChange,
    GetDayReminders,
    AddSessionToAppointmentHour,
  };
};
